/** @jsxImportSource @emotion/react */
import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { css } from '@emotion/react';
import {
  ApiUserOrgRole,
  getNotificationKeysByCategory,
  NOTIFICATION_CATALOG,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CATEGORY_LABELS,
  NotificationKey,
  resolveNotificationConfig,
} from '@kaiizen/kai-pro-api';
import { useAllOrgSettings } from '@kaiizen/kai-pro-api-react-state';
import { useCallback, useMemo } from 'react';

import { useToast } from '@/Client/State';
import { SettingsHeaderRow, SettingsInsightBanner, SettingsToggle } from '@/Client/Widgets';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { ORG_SETTINGS_COLORS, ORG_SETTINGS_SPACING } from './OrgSettingsStyleLib';
import { type SettingsRecord, storesWhatItSends, useSettingsAutosave } from './useSettingsAutosave';

const CONTENT_STYLE = css`
  label: CONTENT_STYLE;
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

const CATEGORY_SECTION_STYLE = css`
  label: CATEGORY_SECTION_STYLE;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid ${ORG_SETTINGS_COLORS.border};
  padding: ${ORG_SETTINGS_SPACING.standardPadding}px ${ORG_SETTINGS_SPACING.gutterX}px 0;

  &:first-of-type {
    border-top: none;
    padding-top: ${ORG_SETTINGS_SPACING.sectionInsetTop}px;
  }
`;

const CATEGORY_HEADER_STYLE = css`
  label: CATEGORY_HEADER_STYLE;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

const CATEGORY_TITLE_STYLE = css`
  label: CATEGORY_TITLE_STYLE;
  font-size: ${pxToRem(FONT_SIZE.bodyInput)};
  font-weight: ${FONT_WEIGHT.regular};
  color: ${ORG_SETTINGS_COLORS.text};
`;

const CATEGORY_DESCRIPTION_STYLE = css`
  label: CATEGORY_DESCRIPTION_STYLE;
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  color: ${ORG_SETTINGS_COLORS.textMuted};
  line-height: 1.4;
`;

const COLUMN_HEADER_ROW_STYLE = css`
  label: COLUMN_HEADER_ROW_STYLE;
  display: grid;
  grid-template-columns: 72px 1fr 140px 360px;
  align-items: center;
  gap: 16px;
  padding: 6px 16px;
  border: 1px solid transparent;
`;

const COLUMN_HEADER_STYLE = css`
  label: COLUMN_HEADER_STYLE;
  font-size: ${pxToRem(FONT_SIZE.bodyMicro)};
  font-weight: ${FONT_WEIGHT.regular};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ORG_SETTINGS_COLORS.textMuted};
`;

const COLUMN_HEADER_CENTER_STYLE = css`
  label: COLUMN_HEADER_CENTER_STYLE;
  font-size: ${pxToRem(FONT_SIZE.bodyMicro)};
  font-weight: ${FONT_WEIGHT.regular};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ORG_SETTINGS_COLORS.textMuted};
  text-align: center;
`;

const NOTIFICATION_ROW_STYLE = css`
  label: NOTIFICATION_ROW_STYLE;
  display: grid;
  grid-template-columns: 72px 1fr 140px 360px;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid ${ORG_SETTINGS_COLORS.border};
  border-radius: 0;
  background: ${ORG_SETTINGS_COLORS.white};
  margin-bottom: 6px;
`;

const TOGGLE_CELL_STYLE = css`
  label: TOGGLE_CELL_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DESCRIPTION_CELL_STYLE = css`
  label: DESCRIPTION_CELL_STYLE;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const NOTIFICATION_TITLE_STYLE = css`
  label: NOTIFICATION_TITLE_STYLE;
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.regular};
  color: ${ORG_SETTINGS_COLORS.text};
`;

const NOTIFICATION_DESC_STYLE = css`
  label: NOTIFICATION_DESC_STYLE;
  font-size: ${pxToRem(FONT_SIZE.bodyMicro)};
  color: ${ORG_SETTINGS_COLORS.textMuted};
  line-height: 1.4;
`;

const EMAIL_CELL_STYLE = css`
  label: EMAIL_CELL_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RECIPIENTS_CELL_STYLE = css`
  label: RECIPIENTS_CELL_STYLE;
  display: flex;
  flex-direction: row;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`;

const CHECK_STYLE = css`
  label: CHECK_STYLE;
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
`;

/**
 * A recipient chip. Selection is the design's lemon wash under ink, and hover its lighter tint -
 * the same pair the Kai Navigator carries selection with. It was a green outline, which is the
 * interim design's way of saying "on" and a colour this one does not use (KAIDEV-4345).
 */
const roleChipStyle = (active: boolean, disabled: boolean) =>
  css`
  label: roleChipStyle;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border-radius: 0;
  font-size: ${pxToRem(FONT_SIZE.bodyMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: ${disabled ? 'default' : 'pointer'};
  user-select: none;
  transition: background-color 0.1s, border-color 0.1s, color 0.1s;
  background: ${active ? COLORS.selectedWash : ORG_SETTINGS_COLORS.white};
  border: 1.5px solid ${active ? ORG_SETTINGS_COLORS.text : ORG_SETTINGS_COLORS.border};
  color: ${active ? ORG_SETTINGS_COLORS.text : ORG_SETTINGS_COLORS.textMuted};
  opacity: ${disabled ? 0.4 : 1};

  ${
    !disabled && !active
      ? `
    &:hover {
      background: ${COLORS.wash};
      border-color: ${ORG_SETTINGS_COLORS.textMuted};
      color: ${ORG_SETTINGS_COLORS.text};
    }
  `
      : ''
  }
`;

type DraftOverride = {
  enabled?: boolean;
  emailEnabled?: boolean;
  recipientRoles?: ApiUserOrgRole[];
};

type DraftOverrides = Partial<Record<NotificationKey, DraftOverride>>;

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  fieldUser: 'Field User',
};

const ALL_DISPLAY_ROLES: ApiUserOrgRole[] = ['admin', 'manager', 'fieldUser'];

/**
 * Org Settings → Notifications (KAIDEV-4345).
 *
 * Every control here is a toggle or a chip - a decision already made - so each saves the moment
 * it is made and the tab carries no save control, no cancel and no navigation guard.
 */
export const NotificationsTabContent = () => {
  const { allOrgSettings, allOrgSettingsMutators, isLoading } = useAllOrgSettings();
  const { showErrorToast } = useToast();

  const persistedOverrides = useMemo(
    () => (allOrgSettings?.notificationSettings?.overrides ?? {}) as SettingsRecord<DraftOverride>,
    [allOrgSettings?.notificationSettings?.overrides],
  );

  /* Rethrown after reporting: the hook reverts the row that failed, and without a word on the
     page that revert would read as the toggle flipping back on its own. */
  const save = useCallback(async (record: SettingsRecord<DraftOverride>) => {
    try {
      await allOrgSettingsMutators.update({ notificationSettings: { overrides: record } });
    } catch (error) {
      showErrorToast(error, 'save notification settings');
      throw error;
    }
  }, [allOrgSettingsMutators, showErrorToast]);

  const { draft, updateEntry } = useSettingsAutosave<DraftOverride>(persistedOverrides, { save, toStoredForm: storesWhatItSends });

  /* The draft is the whole override record, so the row reads from it directly rather than
     layering a partial draft over the stored settings. */
  const getEffectiveResolved = (key: NotificationKey) =>
    resolveNotificationConfig(key, {
      ...allOrgSettings,
      notificationSettings: { overrides: draft as DraftOverrides },
    });

  const updateOverride = (key: NotificationKey, updates: DraftOverride) => updateEntry(key, { ...(draft[key] ?? {}), ...updates }, { immediate: true });

  const handleToggleEnabled = (key: NotificationKey) => updateOverride(key, { enabled: !getEffectiveResolved(key).enabled });

  const handleToggleEmailEnabled = (key: NotificationKey) => updateOverride(key, { emailEnabled: !getEffectiveResolved(key).emailEnabled });

  const handleToggleRole = (key: NotificationKey, role: ApiUserOrgRole) => {
    const { recipientRoles } = getEffectiveResolved(key);
    updateOverride(key, {
      recipientRoles: recipientRoles.includes(role)
        ? recipientRoles.filter(candidate => candidate !== role)
        : [...recipientRoles, role],
    });
  };

  return (
    <>
      <div css={CONTENT_STYLE}>
        <SettingsInsightBanner dismissCookieKey='org-settings-notifications-insight-dismissed' />
        {
          /* No save control: every change here is already stored. */
        }
        <SettingsHeaderRow
          title='Notification Settings'
          counts={{ lead: `${Object.keys(NOTIFICATION_CATALOG).length} notifications` }}
          hideDefaultActions
        />

        {NOTIFICATION_CATEGORIES.map(category => {
          const notificationKeys = getNotificationKeysByCategory(category);
          if (notificationKeys.length === 0) return null;
          const categoryLabel = NOTIFICATION_CATEGORY_LABELS[category];

          return (
            <div key={category} css={CATEGORY_SECTION_STYLE}>
              <div css={CATEGORY_HEADER_STYLE}>
                <div css={CATEGORY_TITLE_STYLE}>{categoryLabel.title}</div>
                <div css={CATEGORY_DESCRIPTION_STYLE}>{categoryLabel.description}</div>
              </div>

              <div css={COLUMN_HEADER_ROW_STYLE}>
                <div css={COLUMN_HEADER_STYLE}>On/Off</div>
                <div css={COLUMN_HEADER_STYLE}>Description</div>
                <div css={COLUMN_HEADER_CENTER_STYLE}>Receive Email</div>
                <div css={COLUMN_HEADER_STYLE}>Recipients</div>
              </div>

              {notificationKeys.map(notificationKey => {
                const def = NOTIFICATION_CATALOG[notificationKey];
                const resolved = getEffectiveResolved(notificationKey);
                const isLocked = !!(def as any).locked;
                const availableRoles: ApiUserOrgRole[] = (def as any).availableRecipientRoles ?? ALL_DISPLAY_ROLES;
                return (
                  <div key={notificationKey} css={NOTIFICATION_ROW_STYLE}>
                    <div css={TOGGLE_CELL_STYLE}>
                      <SettingsToggle
                        checked={resolved.enabled}
                        onClick={() => handleToggleEnabled(notificationKey)}
                        disabled={isLoading || isLocked}
                        aria-label={`Enable ${def.displayName}`}
                      />
                    </div>

                    <div css={DESCRIPTION_CELL_STYLE}>
                      <div css={NOTIFICATION_TITLE_STYLE}>{def.displayName}</div>
                      <div css={NOTIFICATION_DESC_STYLE}>{def.description}</div>
                    </div>

                    <div css={EMAIL_CELL_STYLE}>
                      <SettingsToggle
                        checked={resolved.emailEnabled}
                        onClick={() => handleToggleEmailEnabled(notificationKey)}
                        disabled={isLoading || isLocked || !resolved.enabled}
                        aria-label={`Email for ${def.displayName}`}
                      />
                    </div>

                    <div css={RECIPIENTS_CELL_STYLE}>
                      {ALL_DISPLAY_ROLES.filter(r => availableRoles.includes(r)).map(role => {
                        const active = resolved.recipientRoles.includes(role);
                        const disabled = isLoading || isLocked || !resolved.enabled;
                        return (
                          <button
                            key={role}
                            css={roleChipStyle(active, disabled)}
                            onClick={() => !disabled && handleToggleRole(notificationKey, role)}
                            type='button'
                            aria-pressed={active}
                          >
                            {active && <span css={CHECK_STYLE}>✓</span>}
                            {ROLE_LABELS[role] ?? role}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
};
